// Admin authentication configuration
const ADMIN_SECRET = import.meta.env.VITE_JWT_SECRET || 'lbtiling-admin-secret-key-2024';
const TOKEN_KEY = 'lbtiling-admin-token';

// Simple admin token structure (base64 encoded JSON with signature)
export interface AdminTokenPayload {
  userId: string;
  email: string;
  role: 'admin';
  iat: number; // issued at (timestamp)
  exp: number; // expires at (timestamp)
}

/**
 * Simple base64 encoding
 */
function base64UrlEncode(str: string): string {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Simple base64 decoding
 */
function base64UrlDecode(str: string): string {
  str = (str + '===').slice(0, str.length + (str.length % 4));
  return atob(str.replace(/-/g, '+').replace(/_/g, '/'));
}

/**
 * Create a simple hash for signature verification
 */
function createSignature(payload: string): string {
  // Simple hash function (not cryptographically secure, but sufficient for admin-only use)
  let hash = 0;
  const input = payload + ADMIN_SECRET;
  
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
}

/**
 * Generate a long-lived admin token
 * Use this function to create your initial admin token
 */
export function generateAdminToken(): string {
  const now = Math.floor(Date.now() / 1000);
  const oneYear = 365 * 24 * 60 * 60; // 1 year in seconds
  
  const payload: AdminTokenPayload = {
    userId: 'admin-001',
    email: 'admin@lbtiling.ca',
    role: 'admin',
    iat: now,
    exp: now + oneYear
  };

  const payloadStr = JSON.stringify(payload);
  const encodedPayload = base64UrlEncode(payloadStr);
  const signature = createSignature(encodedPayload);
  
  return `${encodedPayload}.${signature}`;
}

/**
 * Verify and decode admin token
 */
export function verifyAdminToken(token: string): AdminTokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 2) {
      return null;
    }

    const [encodedPayload, signature] = parts;
    
    // Verify signature
    const expectedSignature = createSignature(encodedPayload);
    if (signature !== expectedSignature) {
      console.warn('Invalid token signature');
      return null;
    }

    // Decode payload
    const payloadStr = base64UrlDecode(encodedPayload);
    const payload: AdminTokenPayload = JSON.parse(payloadStr);
    
    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      console.warn('Token has expired');
      return null;
    }

    // Verify role
    if (payload.role !== 'admin') {
      console.warn('Invalid role in token');
      return null;
    }
    
    return payload;
  } catch (error) {
    console.warn('Invalid admin token:', error);
    return null;
  }
}

/**
 * Store JWT token in localStorage
 */
export function setAdminToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Get JWT token from localStorage
 */
export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Remove JWT token from localStorage
 */
export function removeAdminToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if user is authenticated admin
 */
export function isAdminAuthenticated(): boolean {
  const token = getAdminToken();
  if (!token) return false;
  
  const payload = verifyAdminToken(token);
  return payload !== null && payload.role === 'admin';
}

/**
 * Get current admin user info
 */
export function getCurrentAdmin(): AdminTokenPayload | null {
  const token = getAdminToken();
  if (!token) return null;
  
  return verifyAdminToken(token);
}

/**
 * Utility function to log the generated admin token to console
 * Call this once to get your admin token, then remove this call
 */
export function logAdminToken(): void {
  const token = generateAdminToken();
  console.log('=== LB TILING ADMIN TOKEN ===');
  console.log('Copy this token and paste it in your browser console:');
  console.log(`localStorage.setItem('${TOKEN_KEY}', '${token}');`);
  console.log('Token expires in 1 year');
  console.log('=============================');
}